import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getTicket,
  updateTicket,
  deleteTicket,
  listMembers,
  uploadAttachment,
  deleteAttachment,
  attachmentUrl,
  type TicketStatus,
  type TicketPriority,
} from "@/lib/api";
import { STATUS_COLUMNS, STATUS_COLOR, PRIORITY_META, PRIORITY_ORDER } from "@/lib/board";
import { Avatar } from "@/components/Avatar";

const inputCls =
  "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
const labelCls = "mb-1 block text-xs font-medium text-text-muted";

/**
 * Where this ticket sits in the board's reading order, and how to move along
 * it. Optional: the modal works standalone, it just loses its arrows.
 */
export interface TicketNav {
  /** 0-based position in the ordered list. */
  index: number;
  total: number;
  goTo: (index: number) => void;
}

/** Jira-style centered ticket modal: content left, details panel right. */
export function TicketModal({
  ticketId,
  projectKey,
  onClose,
  nav,
}: {
  ticketId: string;
  projectKey: string;
  onClose: () => void;
  nav?: TicketNav;
}) {
  const qc = useQueryClient();
  const { data: ticket } = useQuery({
    queryKey: ["ticket", ticketId],
    queryFn: () => getTicket(ticketId),
  });
  const { data: members } = useQuery({ queryKey: ["members"], queryFn: listMembers });
  const fileRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [solution, setSolution] = useState("");
  const [status, setStatus] = useState<TicketStatus>("todo");
  const [priority, setPriority] = useState<TicketPriority>("medium");
  const [assigneeId, setAssigneeId] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (ticket) {
      setTitle(ticket.title);
      setDescription(ticket.description ?? "");
      setSolution(ticket.solution ?? "");
      setStatus(ticket.status);
      setPriority(ticket.priority);
      setAssigneeId(ticket.assigneeId ?? "");
      setDirty(false);
    }
  }, [ticket]);

  const canPrev = !!nav && nav.index > 0;
  const canNext = !!nav && nav.index < nav.total - 1;

  /**
   * Stepping away discards anything unsaved, so ask first. Closing already
   * behaved this way silently; with arrows it becomes easy to lose an edit by
   * pressing a key twice, which is worth one confirm.
   */
  const step = (delta: number) => {
    if (!nav) return;
    const target = nav.index + delta;
    if (target < 0 || target >= nav.total) return;
    if (dirty && !confirm("Discard unsaved changes to this ticket?")) return;
    nav.goTo(target);
  };

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      // Arrows move the caret while someone is editing — only treat them as
      // navigation when focus is outside a field.
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el?.isContentEditable) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      step(e.key === "ArrowLeft" ? -1 : 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // No dependency array on purpose: the handler closes over `dirty` and
    // `nav`, and `nav` is rebuilt each render by the board. Re-binding one
    // listener per render is cheaper than reasoning about a stale closure
    // that would navigate away from unsaved edits without asking.
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["ticket", ticketId] });
    if (ticket) qc.invalidateQueries({ queryKey: ["tickets", ticket.projectId] });
  };

  const save = useMutation({
    mutationFn: () =>
      updateTicket(ticketId, {
        title,
        description: description || null,
        solution: solution || null,
        status,
        priority,
        assigneeId: assigneeId || null,
      }),
    onSuccess: () => {
      invalidate();
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    },
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadAttachment(ticketId, file),
    onSuccess: invalidate,
  });
  const removeAttachment = useMutation({
    mutationFn: (id: string) => deleteAttachment(id),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => deleteTicket(ticketId),
    onSuccess: () => {
      if (ticket) qc.invalidateQueries({ queryKey: ["tickets", ticket.projectId] });
      onClose();
    },
  });

  const mark = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setDirty(true);
  };

  const modal = (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 backdrop-blur-[2px] sm:p-8"
      onClick={onClose}
    >
      <div
        className="chat-fade flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-token-lg border border-border bg-bg-elevated shadow-token-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex shrink-0 items-center gap-3 border-b border-border bg-bg-card px-6 py-3.5">
          <span className="flex items-center gap-2 font-mono text-sm text-text-muted">
            {ticket && (
              <i
                className={`fa-solid ${PRIORITY_META[priority].icon}`}
                style={{ color: PRIORITY_META[priority].color }}
                title={`${PRIORITY_META[priority].label} priority`}
              />
            )}
            {ticket ? `${projectKey}-${ticket.number}` : "…"}
          </span>
          {ticket && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
              style={{
                background: `${STATUS_COLOR[status]}1a`,
                color: STATUS_COLOR[status],
                border: `1px solid ${STATUS_COLOR[status]}40`,
              }}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: STATUS_COLOR[status] }} />
              {STATUS_COLUMNS.find((c) => c.status === status)?.label}
            </span>
          )}
          <span className="flex-1" />
          <div className="flex items-center gap-2">
            {nav && nav.total > 1 && (
              <div className="mr-1 flex items-center gap-1">
                <button
                  onClick={() => step(-1)}
                  disabled={!canPrev}
                  title="Previous ticket (←)"
                  aria-label="Previous ticket"
                  className="grid h-7 w-7 place-items-center rounded-md text-text-muted transition-colors hover:bg-bg-card-hover hover:text-text-heading disabled:pointer-events-none disabled:opacity-30"
                >
                  <i className="fa-solid fa-chevron-left text-xs" />
                </button>
                <span className="min-w-[3.5rem] text-center font-mono text-[11px] tabular-nums text-text-muted">
                  {nav.index + 1} / {nav.total}
                </span>
                <button
                  onClick={() => step(1)}
                  disabled={!canNext}
                  title="Next ticket (→)"
                  aria-label="Next ticket"
                  className="grid h-7 w-7 place-items-center rounded-md text-text-muted transition-colors hover:bg-bg-card-hover hover:text-text-heading disabled:pointer-events-none disabled:opacity-30"
                >
                  <i className="fa-solid fa-chevron-right text-xs" />
                </button>
              </div>
            )}
            <button
              onClick={() => {
                if (confirm("Delete this ticket? This cannot be undone.")) remove.mutate();
              }}
              className="rounded-md px-2 py-1 text-xs text-danger hover:bg-danger/10"
            >
              <i className="fa-solid fa-trash mr-1" />
              Delete
            </button>
            <button
              onClick={onClose}
              className="grid h-7 w-7 place-items-center rounded-md text-text-muted hover:bg-bg-card-hover hover:text-text-heading"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </div>

        {!ticket ? (
          <div className="grid min-h-[16rem] flex-1 place-items-center text-text-muted">
            <i className="fa-solid fa-circle-notch fa-spin text-xl" />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="grid gap-8 p-6 md:grid-cols-[minmax(0,1fr)_240px]">
              {/* left: content */}
              <div className="min-w-0">
                <input
                  className="mb-5 w-full rounded-token-md border border-transparent bg-transparent px-2 py-1 font-display text-2xl font-semibold leading-snug text-text-heading outline-none transition-colors -mx-2 hover:border-border focus:border-accent"
                  value={title}
                  onChange={(e) => mark(setTitle)(e.target.value)}
                />

                <label className={labelCls}>
                  <i className="fa-solid fa-align-left mr-1.5 text-[0.6rem]" />
                  Description
                </label>
                <textarea
                  className={`${inputCls} mb-5`}
                  rows={6}
                  value={description}
                  onChange={(e) => mark(setDescription)(e.target.value)}
                />

                <label className={labelCls}>
                  <i className="fa-solid fa-circle-check mr-1.5 text-[0.6rem]" />
                  Solution
                </label>
                <textarea
                  className={`${inputCls} mb-5`}
                  rows={4}
                  placeholder="How was this resolved?"
                  value={solution}
                  onChange={(e) => mark(setSolution)(e.target.value)}
                />

                {/* attachments */}
                <div className="mb-1">
                  <div className="mb-2 flex items-center justify-between">
                    <label className={labelCls}>
                      <i className="fa-solid fa-paperclip mr-1.5 text-[0.6rem]" />
                      Attachments
                    </label>
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="text-xs font-medium text-accent hover:underline"
                      disabled={upload.isPending}
                    >
                      {upload.isPending ? "Uploading…" : "+ Add file"}
                    </button>
                    <input
                      ref={fileRef}
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) upload.mutate(f);
                        e.target.value = "";
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {ticket.attachments?.map((a) => (
                      <div
                        key={a.id}
                        className="group relative rounded-token-md border border-border bg-bg-card p-2 transition-all hover:border-accent/60 hover:shadow-token-sm"
                      >
                        {a.mime.startsWith("image/") ? (
                          <a href={attachmentUrl(a.id)} target="_blank" rel="noreferrer">
                            <img
                              src={attachmentUrl(a.id)}
                              alt={a.filename}
                              className="h-20 w-full rounded object-cover"
                            />
                          </a>
                        ) : (
                          <a
                            href={attachmentUrl(a.id)}
                            target="_blank"
                            rel="noreferrer"
                            className="flex h-20 flex-col items-center justify-center text-text-secondary hover:text-accent"
                          >
                            <i className="fa-solid fa-file text-xl" />
                          </a>
                        )}
                        <p
                          className="mt-1 truncate text-[11px] text-text-muted"
                          title={a.filename}
                        >
                          {a.filename}
                        </p>
                        <button
                          onClick={() => removeAttachment.mutate(a.id)}
                          className="absolute right-1 top-1 hidden h-5 w-5 place-items-center rounded bg-black/60 text-[10px] text-white group-hover:grid"
                          title="Remove"
                        >
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </div>
                    ))}
                    {(!ticket.attachments || ticket.attachments.length === 0) && (
                      <p className="col-span-full text-xs text-text-muted">
                        No files attached.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* right: details panel */}
              <aside className="h-fit rounded-token-md border border-border bg-bg-card p-4 md:sticky md:top-0">
                <p className="mb-3 text-[0.6rem] font-semibold uppercase tracking-wider text-text-muted">
                  Details
                </p>
                <label className={labelCls}>Status</label>
                <select
                  className={`${inputCls} mb-3`}
                  value={status}
                  onChange={(e) => mark(setStatus)(e.target.value as TicketStatus)}
                >
                  {STATUS_COLUMNS.map((s) => (
                    <option key={s.status} value={s.status}>
                      {s.label}
                    </option>
                  ))}
                </select>

                <label className={labelCls}>Priority</label>
                <select
                  className={`${inputCls} mb-3`}
                  value={priority}
                  onChange={(e) => mark(setPriority)(e.target.value as TicketPriority)}
                >
                  {PRIORITY_ORDER.map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_META[p].label}
                    </option>
                  ))}
                </select>

                <label className={labelCls}>Assignee</label>
                <select
                  className={inputCls}
                  value={assigneeId}
                  onChange={(e) => mark(setAssigneeId)(e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {members?.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>

                {ticket.assignee && (
                  <div className="mt-3 flex items-center gap-2 border-t border-border-light pt-3">
                    <Avatar
                      name={ticket.assignee.name}
                      url={ticket.assignee.avatarUrl}
                      size={24}
                    />
                    <span className="truncate text-xs text-text-secondary">
                      {ticket.assignee.name}
                    </span>
                  </div>
                )}
                {ticket.assignedBy && (
                  <div className="mt-3 border-t border-border-light pt-3">
                    <p className={labelCls}>Assigned by</p>
                    <div className="flex items-center gap-2">
                      <Avatar
                        name={ticket.assignedBy.name}
                        url={ticket.assignedBy.avatarUrl}
                        size={24}
                      />
                      <span className="truncate text-xs text-text-secondary">
                        {ticket.assignedBy.name}
                      </span>
                    </div>
                  </div>
                )}
                <div className="mt-3 space-y-1 border-t border-border-light pt-3 text-[11px] text-text-muted">
                  <p>Created {new Date(ticket.createdAt).toLocaleDateString()}</p>
                  <p>Updated {new Date(ticket.updatedAt).toLocaleDateString()}</p>
                </div>
              </aside>
            </div>
          </div>
        )}

        {/* footer save bar */}
        {ticket && (
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border bg-bg-card px-6 py-3">
            {saved && (
              <span className="text-xs text-accent">
                <i className="fa-solid fa-check mr-1" />
                Saved
              </span>
            )}
            {dirty && !saved && <span className="text-xs text-text-muted">Unsaved changes</span>}
            <button
              onClick={() => save.mutate()}
              disabled={!dirty || save.isPending}
              className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ color: "var(--color-on-accent)" }}
            >
              {save.isPending ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
